import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { SortableTableHead, sortData } from '../ui/sortable-table';
import {
  RefreshCw,
  Search,
  FileText,
  Users,
  Eye,
  LogIn,
  LogOut,
  Settings,
  Shield,
  AlertTriangle,
  Clock,
  Download,
  Trash2,
  MoreHorizontal,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_ICONS = {
  login: LogIn,
  logout: LogOut,
  login_failed: AlertTriangle,
  view_customer: Eye,
  view_customers: Eye,
  view_user: Eye,
  impersonate_start: Users,
  impersonate_end: Users,
  reset_password: Shield,
  mfa_enabled: Shield,
  add_note: FileText,
  invite_staff: Users,
  update_staff: Settings,
  delete_staff: AlertTriangle,
  create_feature_flag: Settings,
  update_feature_flag: Settings,
  create_ticket: FileText,
  update_ticket: FileText,
  assign_ticket: Users,
  change_plan: Settings,
  issue_refund: AlertTriangle,
};

const ACTION_COLORS = {
  login: 'text-emerald-400',
  logout: 'text-muted-foreground',
  login_failed: 'text-red-400',
  view_customer: 'text-blue-400',
  view_customers: 'text-blue-400',
  view_user: 'text-blue-400',
  impersonate_start: 'text-yellow-400',
  impersonate_end: 'text-yellow-400',
  reset_password: 'text-violet-400',
  mfa_enabled: 'text-emerald-400',
  add_note: 'text-muted-foreground',
  invite_staff: 'text-emerald-400',
  update_staff: 'text-blue-400',
  delete_staff: 'text-red-400',
  create_feature_flag: 'text-violet-400',
  update_feature_flag: 'text-violet-400',
  create_ticket: 'text-blue-400',
  update_ticket: 'text-blue-400',
  assign_ticket: 'text-cyan-400',
  change_plan: 'text-violet-400',
  issue_refund: 'text-red-400',
};

const ACTION_OPTIONS = [
  { value: 'login', label: 'Login' },
  { value: 'logout', label: 'Logout' },
  { value: 'login_failed', label: 'Login Failed' },
  { value: 'view_customer', label: 'View Customer' },
  { value: 'view_user', label: 'View User' },
  { value: 'impersonate_start', label: 'Impersonate Start' },
  { value: 'impersonate_end', label: 'Impersonate End' },
  { value: 'reset_password', label: 'Reset Password' },
  { value: 'mfa_enabled', label: 'MFA Enabled' },
  { value: 'invite_staff', label: 'Invite Staff' },
  { value: 'update_staff', label: 'Update Staff' },
  { value: 'delete_staff', label: 'Delete Staff' },
  { value: 'create_ticket', label: 'Create Ticket' },
  { value: 'update_ticket', label: 'Update Ticket' },
  { value: 'change_plan', label: 'Change Plan' },
  { value: 'issue_refund', label: 'Issue Refund' },
];

const TARGET_OPTIONS = [
  { value: 'customer', label: 'Customer' },
  { value: 'user', label: 'User' },
  { value: 'admin', label: 'Admin' },
  { value: 'feature_flag', label: 'Feature Flag' },
  { value: 'ticket', label: 'Ticket' },
  { value: 'subscription', label: 'Subscription' },
];

export default function OwnerAdminAuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [targetTypeFilter, setTargetTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState({ key: 'timestamp', direction: 'desc' });
  const [selectedLogs, setSelectedLogs] = useState([]);
  const pageSize = 50;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/owner-admin/audit-log?skip=${page * pageSize}&limit=${pageSize}`;
      if (actionFilter && actionFilter !== 'all') url += `&action=${actionFilter}`;
      if (targetTypeFilter && targetTypeFilter !== 'all') url += `&target_type=${targetTypeFilter}`;

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch audit log:', error);
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, targetTypeFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Filter and sort logs client-side
  const filteredAndSortedLogs = useMemo(() => {
    let filtered = logs;
    
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(log => 
        log.admin_email?.toLowerCase().includes(searchLower) ||
        log.action?.toLowerCase().includes(searchLower) ||
        log.target_id?.toLowerCase().includes(searchLower) ||
        log.target_type?.toLowerCase().includes(searchLower) ||
        log.ip_address?.toLowerCase().includes(searchLower)
      );
    }
    
    return sortData(filtered, sort);
  }, [logs, search, sort]);

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    const Icon = ACTION_ICONS[action] || FileText;
    const color = ACTION_COLORS[action] || 'text-muted-foreground';
    return <Icon className={`h-4 w-4 ${color}`} />;
  };

  const formatAction = (action) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Bulk selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLogs(filteredAndSortedLogs.map(log => log.log_id));
    } else {
      setSelectedLogs([]);
    }
  };

  const handleSelectLog = (logId, checked) => {
    if (checked) {
      setSelectedLogs(prev => [...prev, logId]);
    } else {
      setSelectedLogs(prev => prev.filter(id => id !== logId));
    }
  };

  const isAllSelected = filteredAndSortedLogs.length > 0 && 
    selectedLogs.length === filteredAndSortedLogs.length;

  // Bulk actions
  const handleExportSelected = () => {
    const selectedData = filteredAndSortedLogs.filter(log => selectedLogs.includes(log.log_id));
    const csvContent = [
      ['Time', 'Admin', 'Action', 'Target Type', 'Target ID', 'IP Address'].join(','),
      ...selectedData.map(log => [
        formatDateTime(log.timestamp),
        log.admin_email,
        log.action,
        log.target_type || '',
        log.target_id || '',
        log.ip_address
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${selectedData.length} log entries`);
    setSelectedLogs([]);
  };

  const clearSelection = () => {
    setSelectedLogs([]);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Audit Log</h1>
          <p className="text-muted-foreground">Track all admin actions and system events</p>
        </div>
        <Button variant="outline" onClick={fetchLogs} className="border-white/20 text-muted-foreground/80">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Bulk Actions Bar */}
      {selectedLogs.length > 0 && (
        <Card className="bg-emerald-500/10 border-emerald-500/30">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge className="bg-emerald-500/20 text-emerald-400">
                  {selectedLogs.length} selected
                </Badge>
                <span className="text-muted-foreground text-sm">
                  {selectedLogs.length === filteredAndSortedLogs.length 
                    ? 'All logs on this page selected' 
                    : `${selectedLogs.length} of ${filteredAndSortedLogs.length} logs selected`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleExportSelected}
                  className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Selected
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={clearSelection}
                  className="text-muted-foreground"
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="bg-card border-white/15">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by admin, action, target, IP..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white"
                data-testid="audit-log-search"
              />
            </div>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-[180px] bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {ACTION_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={targetTypeFilter} onValueChange={setTargetTypeFilter}>
              <SelectTrigger className="w-[180px] bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Target Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Targets</SelectItem>
                {TARGET_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Log Table */}
      <Card className="bg-card border-white/15">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-12 w-full bg-white/10" />
              ))}
            </div>
          ) : filteredAndSortedLogs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No audit logs found</p>
              {search && (
                <p className="text-muted-foreground text-sm mt-1">
                  Try adjusting your search or filters
                </p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/15 hover:bg-white/[0.07]">
                  <TableHead className="w-[40px]">
                    <Checkbox 
                      checked={isAllSelected}
                      onCheckedChange={handleSelectAll}
                      className="border-white/20"
                    />
                  </TableHead>
                  <SortableTableHead 
                    sortKey="timestamp" 
                    currentSort={sort} 
                    onSort={setSort}
                  >
                    Time
                  </SortableTableHead>
                  <SortableTableHead 
                    sortKey="admin_email" 
                    currentSort={sort} 
                    onSort={setSort}
                  >
                    Admin
                  </SortableTableHead>
                  <SortableTableHead 
                    sortKey="action" 
                    currentSort={sort} 
                    onSort={setSort}
                    filterOptions={ACTION_OPTIONS}
                    currentFilter={actionFilter !== 'all' ? actionFilter : null}
                    onFilter={(val) => setActionFilter(val || 'all')}
                  >
                    Action
                  </SortableTableHead>
                  <SortableTableHead 
                    sortKey="target_type" 
                    currentSort={sort} 
                    onSort={setSort}
                    filterOptions={TARGET_OPTIONS}
                    currentFilter={targetTypeFilter !== 'all' ? targetTypeFilter : null}
                    onFilter={(val) => setTargetTypeFilter(val || 'all')}
                  >
                    Target
                  </SortableTableHead>
                  <SortableTableHead 
                    sortKey="ip_address" 
                    currentSort={sort} 
                    onSort={setSort}
                  >
                    IP Address
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedLogs.map((log, index) => (
                  <TableRow 
                    key={log.log_id || index} 
                    className={`border-white/15 hover:bg-white/[0.07] ${
                      selectedLogs.includes(log.log_id) ? 'bg-emerald-500/5' : ''
                    }`}
                  >
                    <TableCell>
                      <Checkbox 
                        checked={selectedLogs.includes(log.log_id)}
                        onCheckedChange={(checked) => handleSelectLog(log.log_id, checked)}
                        className="border-white/20"
                      />
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDateTime(log.timestamp)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-white text-sm">{log.admin_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getActionIcon(log.action)}
                        <span className="text-white">{formatAction(log.action)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {log.target_type ? (
                        <div>
                          <Badge variant="outline" className="border-white/20 text-muted-foreground text-xs">
                            {log.target_type}
                          </Badge>
                          {log.target_id && (
                            <p className="text-xs text-muted-foreground mt-1 font-mono truncate max-w-[150px]" title={log.target_id}>
                              {log.target_id}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground font-mono text-xs">
                      {log.ip_address}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="border-white/20 text-muted-foreground/80"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * pageSize >= total}
              className="border-white/20 text-muted-foreground/80"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
